import videojs from 'video.js';
import chromecast from '@silvermine/videojs-chromecast';
import airplay from '@silvermine/videojs-airplay';
import 'videojs-contrib-eme';
import 'video.js/dist/video-js.css';
import { base64DecodeUint8Array, arrayToString, base64EncodeUint8Array, getExtension } from '@content-technology-partners-ltd/shared-util';
import axios from 'axios';

const stopChromeCasting = (window)=>{
    const castSession = window.cast?.framework?.CastContext?.getInstance()?.getCurrentSession();
    castSession?.endSession(true);
};

class PallyconDrmStrategy {
    async getDrmConfig(streamUrl, drmId, userUid) {
        const token = await this.getDrmToken(drmId, this.drmType, userUid);
        if (!token) {
            throw new Error(`Drm type not supported: ${this.drmType}`);
        }
        switch(this.drmType){
            case 'FairPlay':
                return this.getFairPlayConfig(streamUrl, token);
            case 'PlayReady':
                return this.getPlayReadyConfig(streamUrl, token);
            case 'Widevine':
                return this.getWideVineConfig(streamUrl, token);
            default:
                throw new Error(`Drm type not supported: ${this.drmType}`);
        }
    }
    async getWideVineConfig(streamUrl, token) {
        return {
            type: 'application/dash+xml',
            src: streamUrl,
            keySystems: {
                'com.widevine.alpha': {
                    url: this.PALLYCON_LICENSE_URI,
                    licenseHeaders: {
                        'pallycon-customdata-v2': token
                    }
                }
            }
        };
    }
    async getPlayReadyConfig(streamUrl, token) {
        return {
            type: 'application/dash+xml',
            src: streamUrl,
            keySystems: {
                'com.microsoft.playready': {
                    url: this.PALLYCON_LICENSE_URI,
                    licenseHeaders: {
                        'pallycon-customdata-v2': token
                    }
                }
            }
        };
    }
    async getFairPlayConfig(streamUrl, token) {
        return {
            type: 'application/x-mpegurl',
            src: streamUrl,
            keySystems: {
                'com.apple.fps.1_0': {
                    getCertificate: (_emeOptions, callback)=>{
                        videojs.xhr({
                            url: this.FAIR_PLAY_CERT_URI,
                            method: 'GET'
                        }, (err, _response, responseBody)=>{
                            if (err) {
                                callback(err);
                                return;
                            }
                            callback(null, base64DecodeUint8Array(responseBody));
                        });
                    },
                    getContentId: function(_emeOptions, initData) {
                        const contentId = arrayToString(initData);
                        return contentId.substring(contentId.indexOf('skd://') + 6);
                    },
                    getLicense: (_emeOptions, _contentId, keyMessage, callback)=>{
                        videojs.xhr({
                            url: this.PALLYCON_LICENSE_URI,
                            method: 'POST',
                            responseType: 'text',
                            body: 'spc=' + base64EncodeUint8Array(keyMessage),
                            headers: {
                                'Content-type': 'application/x-www-form-urlencoded',
                                'pallycon-customdata-v2': token
                            }
                        }, (err, _response, responseBody)=>{
                            if (err) {
                                callback(err);
                                return;
                            }
                            callback(null, base64DecodeUint8Array(responseBody));
                        });
                    }
                }
            }
        };
    }
    constructor(drmType, getDrmToken){
        this.drmType = drmType;
        this.getDrmToken = getDrmToken;
        this.PALLYCON_LICENSE_URI = 'https://license.pallycon.com/ri/licenseManager.do';
        this.FAIR_PLAY_CERT_URI = 'https://license-global.pallycon.com/ri/fpsKeyManager.do?siteId=BCVO';
        this.drmProviderName = 'pallycon';
    }
}

class BuyDrmStrategy {
    async getDrmConfig(streamUrl) {
        return this.drmType === 'FairPlay' ? this.getFairPlayConfig(streamUrl) : this.getWideVineOrPlayReadyConfig(streamUrl);
    }
    async getWideVineOrPlayReadyConfig(streamUrl) {
        return {
            type: 'application/dash+xml',
            src: streamUrl,
            keySystems: {
                'com.widevine.alpha': {
                    url: this.buyDrmProxy + '?drm-type=widevine',
                    audioRobustness: 'SW_SECURE_CRYPTO',
                    videoRobustness: 'SW_SECURE_CRYPTO'
                },
                'com.microsoft.playready': {
                    url: this.buyDrmProxy + '?drm-type=playready'
                }
            }
        };
    }
    async getFairPlayConfig(streamUrl) {
        return {
            type: 'application/x-mpegurl',
            src: streamUrl,
            keySystems: {
                'com.apple.fps.1_0': {
                    certificateUri: this.buyDrmCert,
                    getContentId: function(_emeOption, initData) {
                        const contentId = arrayToString(initData);
                        return contentId.split('skd://')[1].substring(0, 32);
                    },
                    getLicense: (_emeOptions, _contentId, keyMessage, callback)=>{
                        videojs.xhr({
                            url: this.buyDrmProxy + '?drm-type=fairplay',
                            method: 'POST',
                            responseType: 'text',
                            body: 'spc=' + base64EncodeUint8Array(keyMessage) + '&assetId=' + _contentId,
                            headers: {
                                'Content-type': 'application/x-www-form-urlencoded'
                            }
                        }, (err, _response, responseBody)=>{
                            if (err) {
                                callback(err);
                                return;
                            }
                            callback(null, base64DecodeUint8Array(responseBody));
                        });
                    }
                }
            }
        };
    }
    constructor(drmType, buyDrmCert, buyDrmProxy){
        this.drmType = drmType;
        this.buyDrmCert = buyDrmCert;
        this.buyDrmProxy = buyDrmProxy;
        this.drmProviderName = 'buydrm';
    }
}

const getDrmToken = (drmEndpointKey, drmBaseUrl)=>async (uid, tokenType, userUid)=>{
        const config = {
            headers: {
                'X-API-KEY': drmEndpointKey
            }
        };
        const res = await axios.get(`${drmBaseUrl}?uid=${uid}&drm=${tokenType}&userId=${userUid}`, config);
        return res.data.token;
    };

const drmProvider = (drmProviderInfo, drmType, pallyconDrmEndpointKey, pallyconDrmLicenceBaseUrl)=>{
    switch(drmProviderInfo.provider){
        case 'buydrm':
            return new BuyDrmStrategy(drmType, drmProviderInfo.buydrm_certificate, drmProviderInfo.buydrm_proxy);
        case 'pallycon':
            if (pallyconDrmEndpointKey && pallyconDrmLicenceBaseUrl) {
                return new PallyconDrmStrategy(drmType, getDrmToken(pallyconDrmEndpointKey, pallyconDrmLicenceBaseUrl));
            }
            throw new Error(`DRM provider ${drmProviderInfo.provider} requires an endpoint key and base url`);
        default:
            throw new Error(`DRM provider not supported: ${drmProviderInfo.provider}`);
    }
};

const errorCodeDescription = new Map([
    [
        0,
        'MEDIA_ERR_CUSTOM'
    ],
    [
        1,
        'MEDIA_ERR_ABORTED'
    ],
    [
        2,
        'MEDIA_ERR_NETWORK'
    ],
    [
        3,
        'MEDIA_ERR_DECODE'
    ],
    [
        4,
        'MEDIA_ERR_SRC_NOT_SUPPORTED'
    ],
    [
        5,
        'MEDIA_ERR_ENCRYPTED'
    ]
]);
const errorCodeMessage = new Map([
    [
        0,
        ''
    ],
    [
        1,
        'You aborted the media playback'
    ],
    [
        2,
        'A network error caused the media download to fail part-way.'
    ],
    [
        3,
        'The media playback was aborted due to a corruption problem or because the media used features your browser did not support.'
    ],
    [
        4,
        'The media could not be loaded, either because the server or network failed or because the format is not supported.'
    ],
    [
        5,
        'The media is encrypted and we do not have the keys to decrypt it.'
    ]
]);
/**
 * `MediaPlayerError` wraps `videojs.MediaError` and `MediaError`
 */ // eslint-disable-next-line no-use-before-define
class MediaPlayerError {
    constructor(value){
        /**
   * The expanded `MediaError` code, defaults to `0`, a custom error.
   */ this.code = 0;
        /**
   * An optional message that to show with the error. Message is not part of the HTML5
   * video spec but allows for more informative custom errors.
   */ this.message = '';
        if (value instanceof MediaPlayerError) {
            return value;
        }
        if (typeof value === 'string') {
            this.code = 0;
            this.message = value;
        } else if (typeof value === 'number') {
            this.code = value;
            this.message = errorCodeMessage.get(value) ?? '';
        } else if (typeof value === 'object') {
            this.code = value.code;
            this.message = value.message;
        }
        errorCodeDescription.forEach((value, key)=>{
            this[value] = key;
        });
    }
}
errorCodeDescription.forEach((value, key)=>{
    MediaPlayerError[value] = key;
});

chromecast(videojs);
airplay(videojs);
const getRewindButton = (onClick)=>{
    const button = videojs.getComponent('Button');
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return videojs.extend(button, {
        constructor: function(...args) {
            button.apply(this, args);
            this.controlText('Go back');
            this.addClass('vjs-go-back-button');
        },
        handleClick: onClick
    });
};
const getChatButton = (onClick)=>{
    const button = videojs.getComponent('Button');
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return videojs.extend(button, {
        constructor: function(...args) {
            button.apply(this, args);
            this.controlText('Live Chat');
            this.addClass('vjs-live-chat-button');
        },
        handleClick: onClick
    });
};
class MediaPlayer extends videojs.getComponent('Player') {
    getConfigForChromecast(playerConfig) {
        return playerConfig ? {
            playerConfig,
            ...this.chromecastConfig
        } : {
            ...this.chromecastConfig
        };
    }
    disableTranslationsOnTimeDisplays() {
        this.$('.vjs-current-time-display').setAttribute('translate', 'no');
        this.$('.vjs-remaining-time-display').setAttribute('translate', 'no');
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(videoElement, config){
        super(videoElement, MediaPlayer.VIDEO_JS_CONFIG(config));
        this.useLowQuality = ()=>{
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // eslint-disable-next-line no-underscore-dangle
            this.tech_.options_.vhs.limitRenditionByPlayerDimensions = true;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // eslint-disable-next-line no-underscore-dangle
            this.tech_.options_.vhs.enableLowInitialPlaylist = true;
        };
        this.rewindVideo = ()=>{
            const BACKWARD_TIME_IN_SEC = 15;
            this.currentTime(this.currentTime() - BACKWARD_TIME_IN_SEC);
        };
        this.addRewindButton = ()=>{
            const rewindButton = getRewindButton(this.rewindVideo);
            videojs.registerComponent('rewindButton', rewindButton);
            this.getChild('controlBar')?.addChild('rewindButton', {});
        };
        this.addChatButton = ()=>{
            if (this.toggleChat) {
                const chatButton = getChatButton(this.toggleChat);
                videojs.registerComponent('chatButton', chatButton);
                this.getChild('controlBar')?.addChild('chatButton', {});
            }
        };
        this.playWithHandleError = ()=>{
            const playPromise = this.play();
            // this is for Safari & Chrome, where videos with sound won't autoplay
            // https://developer.chrome.com/blog/autoplay/
            // https://developer.apple.com/documentation/webkit/delivering_video_content_for_safari
            if (playPromise) {
                this.playPromiseState = 'init';
                playPromise.then(()=>{
                    this.playPromiseState = 'success';
                }).catch((error)=>{
                    if (error.name === 'NotSupportedError') {
                        this.error(error.name);
                        this.onError();
                    } else {
                        this.videoElement.muted = true;
                    }
                    this.playPromiseState = 'failed';
                });
            }
        };
        this.replay = ()=>{
            this.currentTime(0);
            this.playWithHandleError();
        };
        this.stop = ()=>{
            this.currentTime(0);
            this.pause();
        };
        this.toggleControls = (showControls)=>{
            this.controls(showControls);
        };
        this.togglePlay = ()=>{
            this.paused() ? this.playWithHandleError() : this.pause();
        };
        this.toggleFullScreen = ()=>{
            this.isFullscreen() ? this.exitFullscreen() : this.requestFullscreen();
        };
        this.toggleOffline = (isOffline)=>{
            this.isOffline = isOffline;
        };
        this.setPreviewOnly = (isPreview, withSound = false)=>{
            this.isPreviewOnly = isPreview;
            this.muted(!withSound);
        };
        this.setPreviewOnlyAndPlay = (isPreview, withSound = false)=>{
            this.setPreviewOnly(isPreview, withSound);
            this.playWithHandleError();
        };
        this.setUpdateVideoProgress = (setProgressPercent)=>{
            this.updateVideoProgress = setProgressPercent;
        };
        this.setPlayingCallback = (callback)=>{
            this.onPlayingCallback = callback;
        };
        this.setOnEndedCallback = (callback)=>{
            this.onEndedCallback = callback;
        };
        this.setOnLoadedDataCallback = (callback)=>{
            this.onLoadedDataCallback = callback;
        };
        this.setOnLoadedMetaDataCallback = (callback)=>{
            this.onLoadedMetaDataCallback = callback;
        };
        this.setOnLoadedCallback = (callback)=>{
            this.onLoadedCallback = callback;
        };
        this.setOnCanPlayCallback = (callback)=>{
            this.onCanPlayCallback = callback;
        };
        this.setOnVolumeChangeCallback = (callback)=>{
            this.onVolumeChangeCallback = callback;
        };
        this.setOnErrorCallback = (callback)=>{
            this.onErrorCallback = callback;
        };
        this.setOnFullscreenChangedCallback = (callback)=>{
            this.onFullscreenChangedCallback = callback;
        };
        this.setOnStreamRecoveringCallback = (callback)=>{
            this.onStreamRecoveringCallback = callback;
        };
        this.setOnStreamRecoveredCallback = (callback)=>{
            this.onStreamRecoveredCallback = callback;
        };
        this.setOnToggleChatCallback = (callback)=>{
            const toggleChat = ()=>{
                if (!this.isChatOpen && this.isFullscreen()) {
                    this.toggleFullScreen();
                }
                this.isChatOpen = !this.isChatOpen;
                callback(this.isChatOpen);
            };
            this.toggleChat = toggleChat;
            this.addChatButton();
        };
        this.setIsBuffering = (buffering)=>{
            this.isBuffering = buffering;
        };
        this.onVideoVolumeChange = ()=>this.onVolumeChangeCallback?.();
        this.onVideoEnded = ()=>this.onEndedCallback?.();
        this.onLoadedData = ()=>this.onLoadedDataCallback?.();
        this.onLoadedMetaData = ()=>this.onLoadedMetaDataCallback?.();
        this.onVideoLoaded = ()=>this.onLoadedCallback?.();
        this.onCanPlay = ()=>this.onCanPlayCallback?.();
        this.clearRefreshInterval = ()=>{
            this.refreshIntervalId && clearInterval(this.refreshIntervalId);
            this.refreshIntervalId = undefined;
        };
        this.loadStreamUrl = (streamUrl)=>{
            const srcExtension = getExtension(streamUrl);
            const srcType = srcExtension === 'm3u8' ? 'application/x-mpegURL' : `video/` + srcExtension;
            this.src({
                type: srcType,
                src: streamUrl
            });
            this.chromecastConfig = this.getConfigForChromecast();
        };
        this.loadDrmStreamUrl = (streamUrl, videoUid, drmConfig, drmType, pallyconDrmEndpointKey = '', pallyconDrmLicenceBaseUrl = '', userUId = '')=>{
            const videoUrl = streamUrl;
            if (this.drmPlayerConfig) {
                this.clearRefreshInterval();
                return this.src(this.drmPlayerConfig);
            }
            // eslint-disable-next-line no-underscore-dangle
            if (!this.activePlugins_?.eme) {
                this.eme?.();
                drmProvider(drmConfig, drmType, pallyconDrmEndpointKey, pallyconDrmLicenceBaseUrl).getDrmConfig(videoUrl, videoUid, userUId).then((playerConfig)=>{
                    this.drmPlayerConfig = playerConfig;
                    this.src(playerConfig);
                }).catch(()=>{
                    this.error(videojs.MediaError.MEDIA_ERR_ENCRYPTED);
                    this.onError();
                });
            }
        };
        this.onVideoProgress = ()=>{
            const time = this.currentTime();
            const endOfBuffer = this.bufferedEnd();
            this.setIsBuffering(endOfBuffer - time <= 0.5);
            const videoDuration = this.isPreviewOnly ? this.previewDurationInSecs : this.videoElement.duration;
            const percent = Math.floor(time / videoDuration * 100);
            if (this.updateVideoProgress) this.updateVideoProgress(percent);
            if (time >= this.previewDurationInSecs && this.isPreviewOnly) {
                this.pause();
            }
        };
        this.onFullscreenChanged = ()=>{
            const isFullScreen = this.isFullscreen();
            this.onFullscreenChangedCallback?.(isFullScreen);
            if (isFullScreen) {
                this.removeClass('closedFullScreen');
                if (this.isChatOpen && this.toggleChat) {
                    this.toggleChat();
                }
            } else {
                this.addClass('closedFullScreen');
            }
        };
        this.onPlay = ()=>{
            this.userActive(true);
            this.chromecastConfig = this.getConfigForChromecast(this.drmPlayerConfig);
        };
        this.onPlaying = ()=>{
            if (this.onPlayingCallback) this.onPlayingCallback();
        };
        this.onRetryPlaylistError = ()=>{
            if (this.refreshIntervalId) {
                return;
            }
            const CHECK_CONNECTION_INTERVAL_MS = 1000;
            this.onStreamRecoveringCallback?.();
            this.refreshIntervalId = setInterval(()=>{
                if (!this.isOffline) {
                    this.onStreamRecoveredCallback?.();
                    this.clearRefreshInterval();
                }
            }, CHECK_CONNECTION_INTERVAL_MS);
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.onEmeDrmStatusChange = (event)=>{
            if (event.status !== 'usable') {
                this.error(videojs.MediaError.MEDIA_ERR_ENCRYPTED);
                this.onError();
            }
        };
        this.onError = ()=>{
            const error = this.error();
            if (error) {
                this.onErrorCallback?.(new MediaPlayerError(error));
            }
        };
        this.addEventListeners = ()=>{
            this.on(MediaPlayer.ON_ENDED_EVENT_LISTENER, this.onVideoEnded);
            this.on(MediaPlayer.ON_LOADED_DATA, this.onLoadedData);
            this.on(MediaPlayer.ON_LOADED_META_DATA, this.onLoadedMetaData);
            this.on(MediaPlayer.ON_LOADED_EVENT_LISTENER, this.onVideoLoaded);
            this.on(MediaPlayer.ON_CAN_PLAY, this.onCanPlay);
            this.on(MediaPlayer.ON_VOLUME_CHANGE_LISTENER, this.onVideoVolumeChange);
            this.on(MediaPlayer.ON_PROGRESS_EVENT_LISTENER, this.onVideoProgress);
            this.on(MediaPlayer.ON_FULLSCREEN_CHANGED_LISTENER, this.onFullscreenChanged);
            this.on(MediaPlayer.ON_PLAY_LISTENER, this.onPlay);
            this.on(MediaPlayer.ON_PLAYING_LISTENER, this.onPlaying);
            this.on(MediaPlayer.ON_ERROR, this.onError);
            this.tech(MediaPlayer.DISABLE_TECH_WARNING).on(MediaPlayer.ON_RETRY_PLAYLIST_ERROR, this.onRetryPlaylistError);
            this.tech(true).on(MediaPlayer.ON_KEY_STATUS_CHANGE, this.onEmeDrmStatusChange);
        };
        this.removeEventListeners = ()=>{
            this.off(MediaPlayer.ON_ENDED_EVENT_LISTENER, this.onVideoEnded);
            this.off(MediaPlayer.ON_LOADED_DATA, this.onLoadedData);
            this.off(MediaPlayer.ON_LOADED_META_DATA, this.onLoadedMetaData);
            this.off(MediaPlayer.ON_LOADED_EVENT_LISTENER, this.onVideoLoaded);
            this.off(MediaPlayer.ON_CAN_PLAY, this.onCanPlay);
            this.off(MediaPlayer.ON_VOLUME_CHANGE_LISTENER, this.onVideoVolumeChange);
            this.off(MediaPlayer.ON_PROGRESS_EVENT_LISTENER, this.onVideoProgress);
            this.off(MediaPlayer.ON_FULLSCREEN_CHANGED_LISTENER, this.onFullscreenChanged);
            this.off(MediaPlayer.ON_PLAY_LISTENER, this.onPlay);
            this.off(MediaPlayer.ON_PLAYING_LISTENER, this.onPlaying);
            this.off(MediaPlayer.ON_ERROR, this.onError);
            this.tech(MediaPlayer.DISABLE_TECH_WARNING).off(MediaPlayer.ON_PLAY_LISTENER, this.onRetryPlaylistError);
            this.tech(true).off(MediaPlayer.ON_KEY_STATUS_CHANGE, this.onEmeDrmStatusChange);
        };
        this.destroy = ()=>{
            stopChromeCasting(window);
            this.playPromiseState === 'success' && this.pause();
            this.dispose();
            this.removeEventListeners();
        };
        this.videoElement = videoElement;
        this.isPreviewOnly = true;
        this.drmPlayerConfig = undefined;
        this.previewDurationInSecs = config.previewDuration;
        this.chromecastConfig = config.chromecastConfig ? {
            ...config.chromecastConfig
        } : {
            name: '',
            description: ''
        };
        this.playPromiseState = 'init';
        this.isBuffering = false;
        this.isChatOpen = false;
        this.isOffline = false;
        config.isThumbnailVideo && this.useLowQuality();
        this.addEventListeners();
        this.addRewindButton();
        this.disableTranslationsOnTimeDisplays();
    }
}
MediaPlayer.ON_PROGRESS_EVENT_LISTENER = 'timeupdate';
MediaPlayer.ON_ENDED_EVENT_LISTENER = 'ended';
MediaPlayer.ON_LOADED_DATA = 'loadeddata';
MediaPlayer.ON_LOADED_META_DATA = 'loadedmetadata';
MediaPlayer.ON_LOADED_EVENT_LISTENER = 'canplaythrough';
MediaPlayer.ON_CAN_PLAY = 'canplay';
MediaPlayer.ON_FULLSCREEN_CHANGED_LISTENER = 'fullscreenchange';
MediaPlayer.ON_VOLUME_CHANGE_LISTENER = 'volumechange';
MediaPlayer.ON_PLAY_LISTENER = 'play';
MediaPlayer.ON_PLAYING_LISTENER = 'playing';
MediaPlayer.ON_RETRY_PLAYLIST_ERROR = 'retryplaylist';
MediaPlayer.ON_KEY_STATUS_CHANGE = 'keystatuschange';
MediaPlayer.ON_ERROR = 'error';
// https://github.com/videojs/video.js/issues/2617
MediaPlayer.DISABLE_TECH_WARNING = 'no-warning';
MediaPlayer.VIDEO_JS_CONFIG = (config)=>({
        fluid: true,
        preload: 'auto',
        autoplay: false,
        muted: false,
        controls: true,
        inactivityTimeout: config.inactivityTimeout,
        errorDisplay: false,
        techOrder: [
            'chromecast',
            'html5'
        ],
        plugins: {
            chromecast: {
                receiverAppID: config.chromeCastReceiverId,
                addButtonToControlBar: true
            },
            airPlay: {
                addButtonToControlBar: true
            }
        },
        chromecast: {
            requestTitleFn: ()=>config.chromecastConfig?.name ?? '',
            requestSubtitleFn: ()=>config.chromecastConfig?.description ?? '',
            requestCustomDataFn: ()=>config.chromecastConfig
        },
        html5: {
            // [DOCS](https://github.com/videojs/http-streaming#initialization)
            vhs: {
                enableLowInitialPlaylist: true,
                limitRenditionByPlayerDimensions: false,
                overrideNative: config.vhsConfig.overrideNative || !videojs.browser.IS_ANY_SAFARI,
                useBandwidthFromLocalStorage: config.vhsConfig.useLocalStorage
            },
            nativeAudioTracks: false,
            nativeVideoTracks: false
        }
    });

export { MediaPlayer, MediaPlayerError };
